import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldArray, FieldProps } from 'formik'
import { FormikTextField } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import _ from 'lodash'
import { Proving, TrackHotel } from 'model'
import {
  DayOfWeek,
  DefaultButton,
  IconButton,
  ITag,
  Label,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import shortid, { generate } from 'shortid'
import { ParticipantListItem } from 'views/ParticipantViews'
import { useTrackHotelsQuery } from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

export type ProvingDetailsTrackHotelFormValues = Proving
export const addKeyToArrayObjects = (arr: object[]) => {
  if (arr.length === 0) {
    return []
  }
  const arrWithKeys = arr.map((element) => {
    const clonedObject = _.cloneDeep(element)
    if (!element.hasOwnProperty('key')) {
      // @ts-ignore
      clonedObject.key = shortid.generate()
    }
    return clonedObject
  })
  return arrWithKeys
}
const nonEmptyError = 'Bitte beheben Sie die Fehler im Formular'
export class ProvingDetailsTrackHotelFormSchema {
  constructor(provingDateFrom: string, provingDateTo: string) {
    const hotelArrayValidation = Yup.array().of(
      Yup.object().test('isValid', nonEmptyError, (trackHotel: TrackHotel) => {
        const { hotel, dateFrom, dateTo } = trackHotel
        if (!hotel && !dateFrom && !dateTo) {
          return true
        }
        if (hotel && dateFrom && dateTo) {
          if (dateTo >= dateFrom) {
            return true
          }
        }

        return false
      })
    )

    return Yup.object().shape({
      trackHotels: Yup.object().shape({
        outward: hotelArrayValidation,
        return: hotelArrayValidation,
      }),
    })
  }
}

export interface ProvingDetailsTrackHotelValues {
  Hotel: string
}

export const ProvingDetailsTrackHotelForm: React.FunctionComponent<{
  notification?: boolean
  proving: Partial<Proving>
  participant?: Partial<ParticipantListItem>
}> = ({ notification, proving, participant }) => {
  const { t } = useTranslation()

  const { data, loading, error } = useTrackHotelsQuery({
    variables: {
      provingGroundId:
        proving.provingGround!._id || participant!.provingGroundId,
    },
  })

  // const validateDateFrom = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateFrom as any)
  //   } else {
  //     return new Date(proving.dateFrom)
  //   }
  // }

  // const validateDateTo = () => {
  //   if (participant) {
  //     return new Date(participant.provingDateTo as any)
  //   } else {
  //     return new Date(proving.dateTo)
  //   }
  // }

  const hotels: ITag[] =
    data && !loading
      ? data.resources.map((resource) => ({
          key: resource._id,
          name: resource.title,
        }))
      : []

  return (
    <FormStack>
      {notification && (
        <FieldStack>
          <Label>
            {t(
              'Achtung! Es sind mehrere Einträge ausgewählt! Die Werte werden bei allen ausgewählten Einträgen mit den Werten aus der Formularmaske überschrieben!'
            )}
          </Label>
        </FieldStack>
      )}
      <FlexSpinner loading={loading} error={error}>
        <>
          <Label>{t('field.trackHotel.outward.header')}</Label>
          <FieldArray name="trackHotels.outward">
            {({ form, name, ...helpers }) => {
              const values: {
                _id?: string
                id: string
                hotel: string
                dateFrom: Date
                dateTo: Date
              }[] = _.get(form.values, name)
              return (
                <>
                  <AutoStack tokens={{ childrenGap: 12 }}>
                    {values.map((x: any, i: number) => (
                      <FieldStack horizontal={true} key={x._id ? x._id : x.id}>
                        <FieldStackItem
                          name={`trackHotels.outward[${i}].hotel`}
                          required={true}
                          render={(
                            fieldProps: FieldProps<ProvingDetailsTrackHotelValues>
                          ) => {
                            return (
                              <FormikTagPicker
                                label={t('field.trackHotel.outward.label')}
                                {...fieldProps}
                                tags={hotels}
                                pickerCalloutProps={{ calloutWidth: 400 }}
                              />
                            )
                          }}
                        />
                        <FieldStackItem
                          name={`trackHotels.outward[${i}].dateFrom`}
                          required={true}
                          render={(fieldProps) => (
                            <FormikGraphQLDatePicker
                              label={t('field.trackHotel.dateFrom.label')}
                              {...fieldProps}
                              // minDate={validateDateFrom()}
                              // maxDate={validateDateTo()}
                              firstDayOfWeek={DayOfWeek.Monday}
                              strings={DayPickerStrings}
                              showWeekNumbers={true}
                              formatDate={onFormatDate as any}
                              initialPickerDate={new Date(proving.dateFrom)}
                            />
                          )}
                        />
                        <FieldStackItem
                          name={`trackHotels.outward[${i}].dateTo`}
                          required={true}
                          render={(fieldProps) => (
                            <FormikGraphQLDatePicker
                              label={t('field.trackHotel.dateTo.label')}
                              {...fieldProps}
                              // minDate={validateDateFrom()}
                              // maxDate={validateDateTo()}
                              firstDayOfWeek={DayOfWeek.Monday}
                              strings={DayPickerStrings}
                              showWeekNumbers={true}
                              formatDate={onFormatDate as any}
                              initialPickerDate={new Date(proving.dateFrom)}
                            />
                          )}
                        />
                        <FieldStackItem
                          name={`trackHotels.outward[${i}].note`}
                          label={t('section.proving.field.description')}
                          key={x._id ? x._id : x.id}
                          multiline={false}
                          component={FormikTextField}
                        />
                        <IconButton
                          styles={{
                            root: { color: 'red', marginTop: 'auto' },
                          }}
                          iconProps={{ iconName: 'Clear' }}
                          onClick={() => {
                            form.setFieldValue(
                              `trackHotels.outward[${i}].hotel`,
                              null
                            )
                            form.setFieldValue(
                              `trackHotels.outward[${i}].dateFrom`,
                              null
                            )
                            form.setFieldValue(
                              `trackHotels.outward[${i}].dateTo`,
                              null
                            )
                            form.setFieldValue(
                              `trackHotels.outward[${i}].note`,
                              ''
                            )
                          }}
                        />
                        <IconButton
                          styles={{
                            root: { color: 'red', marginTop: 'auto' },
                          }}
                          iconProps={{ iconName: 'Delete' }}
                          onClick={() => {
                            helpers.remove(i)
                          }}
                        />
                      </FieldStack>
                    ))}
                    <FieldStack horizontal={true}>
                      <DefaultButton
                        text={'+ Hotel Hin'}
                        onClick={() => {
                          if (form.values.trackHotels.outward.length < 10) {
                            helpers.push({
                              id: generate(),
                              hotel: null,
                              dateFrom: null,
                              dateTo: null,
                              note: '',
                            })
                            // form.setFieldError(
                            //   'trackHotels.outward',
                            //   nonEmptyError
                            // )
                          }
                        }}
                        disabled={form.values.trackHotels.outward.length >= 10}
                      />
                    </FieldStack>
                  </AutoStack>
                </>
              )
            }}
          </FieldArray>
          <Label>{t('field.trackHotel.return.header')}</Label>
          <FieldArray name="trackHotels.return">
            {({ form, name, ...helpers }) => {
              const values: {
                _id?: string
                id: string
                hotel: string
                dateFrom: Date
                dateTo: Date
              }[] = _.get(form.values, name)

              return (
                <AutoStack tokens={{ childrenGap: 12 }}>
                  {values.map((x: any, i: number) => (
                    <FieldStack horizontal={true} key={x._id ? x._id : x.id}>
                      <FieldStackItem
                        name={`trackHotels.return[${i}].hotel`}
                        render={(
                          fieldProps: FieldProps<ProvingDetailsTrackHotelValues>
                        ) => (
                          <FormikTagPicker
                            label={t('field.trackHotel.return.label')}
                            {...fieldProps}
                            tags={hotels}
                            pickerCalloutProps={{ calloutWidth: 400 }}
                          />
                        )}
                      />
                      <FieldStackItem
                        name={`trackHotels.return[${i}].dateFrom`}
                        render={(fieldProps) => (
                          <FormikGraphQLDatePicker
                            label={t('field.trackHotel.dateFrom.label')}
                            {...fieldProps}
                            // minDate={validateDateFrom()}
                            // maxDate={validateDateTo()}
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            initialPickerDate={new Date(proving.dateFrom)}
                          />
                        )}
                      />
                      <FieldStackItem
                        name={`trackHotels.return[${i}].dateTo`}
                        render={(fieldProps) => (
                          <FormikGraphQLDatePicker
                            label={t('field.trackHotel.dateTo.label')}
                            {...fieldProps}
                            // minDate={validateDateFrom()}
                            // maxDate={validateDateTo()}
                            firstDayOfWeek={DayOfWeek.Monday}
                            strings={DayPickerStrings}
                            showWeekNumbers={true}
                            formatDate={onFormatDate as any}
                            initialPickerDate={new Date(proving.dateFrom)}
                          />
                        )}
                      />
                      <FieldStackItem
                        name={`trackHotels.return[${i}].note`}
                        label={t('section.proving.field.description')}
                        key={x._id ? x._id : x.id}
                        component={FormikTextField}
                      />
                      <IconButton
                        styles={{
                          root: { color: 'red', marginTop: 'auto' },
                        }}
                        iconProps={{ iconName: 'Clear' }}
                        onClick={() => {
                          form.setFieldValue(
                            `trackHotels.return[${i}].hotel`,
                            null
                          )
                          form.setFieldValue(
                            `trackHotels.return[${i}].dateFrom`,
                            null
                          )
                          form.setFieldValue(
                            `trackHotels.return[${i}].dateTo`,
                            null
                          )
                          form.setFieldValue(
                            `trackHotels.return[${i}].note`,
                            ''
                          )
                        }}
                      />
                      <IconButton
                        styles={{
                          root: { color: 'red', marginTop: 'auto' },
                        }}
                        iconProps={{ iconName: 'Delete' }}
                        onClick={() => {
                          helpers.remove(i)
                        }}
                      />
                    </FieldStack>
                  ))}
                  <FieldStack horizontal={true}>
                    <DefaultButton
                      text={'+ Hotel Rück'}
                      onClick={() => {
                        if (form.values.trackHotels.return.length < 10) {
                          helpers.push({
                            id: generate(),
                            hotel: null,
                            dateFrom: null,
                            dateTo: null,
                            note: '',
                          })
                          // form.setFieldError(
                          //   'trackHotels.return',
                          //   nonEmptyError
                          // )
                        }
                      }}
                      disabled={form.values.trackHotels.return.length >= 10}
                    />
                  </FieldStack>
                </AutoStack>
              )
            }}
          </FieldArray>
        </>
      </FlexSpinner>
    </FormStack>
  )
}
